// src/firebase.js
import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAnalytics, isSupported as analyticsSupported } from "firebase/analytics";
import { getAuth, onAuthStateChanged, signInAnonymously } from "firebase/auth";

// ---- Config (envs first, fallback to your current values)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FB_API_KEY || "AIzaSyBehrhb_qEVx-0rth9bGfdjZChoyi9nkPw",
  authDomain: import.meta.env.VITE_FB_AUTH_DOMAIN || "website-1c833.firebaseapp.com",
  projectId: import.meta.env.VITE_FB_PROJECT_ID || "website-1c833",
  // Storage bucket must be the appspot bucket (not a gs:// URL)
  storageBucket: import.meta.env.VITE_FB_STORAGE_BUCKET || "website-1c833.appspot.com",
  messagingSenderId: import.meta.env.VITE_FB_MESSAGING_SENDER_ID || "851541569752",
  appId: import.meta.env.VITE_FB_APP_ID || "1:851541569752:web:acfde1c7ce1c12f31c67ad",
  measurementId: import.meta.env.VITE_FB_MEASUREMENT_ID || "G-YN9T78FKY8",
};

// ---- Init
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);

// ---- Auth: anonymous sign-in so request.auth is present in rules
const auth = getAuth(app);

// Promise that resolves when a user is available
export const authReady = new Promise((resolve) => {
  onAuthStateChanged(auth, async (user) => {
    if (user) return resolve(user);
    try {
      const cred = await signInAnonymously(auth);
      resolve(cred.user);
    } catch (e) {
      // still resolve so callers don't hang; writes will fail if not authed
      resolve(null);
    }
  });
});

// Helper to await auth before writes
export async function ensureAuth() {
  return (auth.currentUser || (await authReady));
}

// ---- Emulators (optional)
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === "true") {
  try {
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
    connectStorageEmulator(storage, "127.0.0.1", 9199);
    console.info("[Firebase] Using emulators (Firestore:8080, Storage:9199)");
  } catch {}
}

// ---- Analytics (optional)
let analytics = null;
if (typeof window !== "undefined" && firebaseConfig.measurementId) {
  analyticsSupported().then((ok) => {
    if (ok) { try { analytics = getAnalytics(app); } catch {} }
  });
}

export { app, db, storage, auth, analytics };
